import { useLayoutEffect } from 'preact/compat';
import { StateUpdater, useCallback, useMemo, useState } from 'preact/hooks';

const DE = JSON.parse;
const SER = JSON.stringify;

export default function useLocalStorage<T>(k: string, initialValue?: T): [T | undefined, StateUpdater<T>, () => void] {
    if (typeof window === 'undefined') return [initialValue, () => void(0), () => void(0)];
    if (!k) throw new TypeError('LocalStorage keys must not be falsy');
    /* eslint-disable react-hooks/rules-of-hooks */
    const init = useMemo(() => (key: string) => {
        try {
            const val = localStorage.getItem(key);
            if (val != null) return DE(val);

            if (initialValue !== undefined) localStorage.setItem(key, SER(initialValue));
            return initialValue;
        } catch {
            return initialValue
        }
    }, [initialValue]);
    const [state, setState] = useState<T | undefined>(() => init(k));
    useLayoutEffect(() => setState(init(k)), [init, k]);

    return [
        state,
        useCallback(valOrUpdater => {
            try {
                const val = typeof valOrUpdater === 'function' ? (valOrUpdater as (val: T) => T)(state) : valOrUpdater;
                localStorage.setItem(k, SER(val));
                setState(val);
            } catch {}
        }, [k, setState, state]),
        useCallback(() => {
            try {
                localStorage.removeItem(k);
                setState(undefined);
            } catch {}
        }, [k, setState]),
    ];/* eslint-enable react-hooks/rules-of-hooks */
}
